import storefront from "../global/storefront";
import {
  COLLECTION_STYLE_GROUP_INDEX_QUERY,
  PRODUCT_CARD_GROUPING_NODES_QUERY,
  PRODUCT_SWATCH_COLOR_PATTERNS_QUERY,
  STYLE_GROUP_MEMBERS_QUERY,
} from "../global/storefront/queries";
import { forEachConnectionPage } from "../global/storefront/paginate";
import { escapeForQuotedValue } from "../global/storefront/search-query";

const maxVisibleSwatches = 4;

const maxIdsPerNodesRequest = 250;

const maxStyleGroupsPerLookup = 20;
const maxStyleGroupLookupPages = 4;
const maxCollectionIndexPages = 200;

// The collection scan's first page is small so the first swatch fill (and the
// first paint waiting on it) lands fast; the rest use full pages.
const firstIndexPageSize = 100;

const styleGroupIndexCachePrefix = "plp-style-group-index::";
const styleGroupIndexCacheTtlMs = 30 * 60 * 1000;

const cardImageWidth = 600;

const styleGroupTagPrefix = "style_group:";
const excludedProductTag = "exclude-from-search";

export const pendingRevealAttr = "data-pending-reveal";

const hydratedSwatchCache = new Map();

export function chunk(array, size) {
  const chunks = [];

  for (let i = 0; i < array.length; i += size) {
    chunks.push(array.slice(i, i + size));
  }

  return chunks;
}

export function isSwatchProductFromTags(tags) {
  return (
    Array.isArray(tags) &&
    (tags.includes("product_type:Swatch") || tags.includes("Mega Swatches") || tags.includes("Multi Swatch"))
  );
}

// First style_group: tag wins — snippet.product-card.liquid picks the same one.
function getStyleGroupTag(tags) {
  const styleGroupTag = Array.isArray(tags) ? tags.find((tag) => tag.startsWith(styleGroupTagPrefix)) : null;

  return styleGroupTag ? styleGroupTag.slice(styleGroupTagPrefix.length).trim() : "";
}

export function isExcludedProduct(node) {
  return Array.isArray(node?.tags) && node.tags.includes(excludedProductTag);
}

export function isHomeTryOnType(productType) {
  return window.handleize(String(productType || "")) === "home-try-on";
}

// Products sharing a style_group: tag collapse into one card. Home Try-On is
// always its own card, and swatch products never share a card with garments
// even when they carry the same style_group.
function styleGroupKey({ styleGroup, productType, isSwatchProduct }) {
  const normalized = String(styleGroup || "")
    .trim()
    .toLowerCase();

  if (!normalized || isHomeTryOnType(productType)) {
    return null;
  }

  return `${isSwatchProduct ? "swatch" : "product"}::${normalized}`;
}

function styleGroupKeyForNode(node) {
  return styleGroupKey({
    styleGroup: getStyleGroupTag(node?.tags),
    productType: node?.productType,
    isSwatchProduct: isSwatchProductFromTags(node?.tags),
  });
}

function styleGroupKeyForCard(card) {
  return styleGroupKey({
    styleGroup: card.dataset.styleGroup,
    productType: card.dataset.productType,
    isSwatchProduct: card.dataset.swatchProduct === "true",
  });
}

// --- Product Group metafield verification ---------------------------------
//
// The style_group: tag is the fast guess used for first paint. The Product
// Group metafield is the source of truth: once its metaobjects load (in the
// background), every product gets a canonical key and the grid is rearranged
// only where the two disagree (see verifyGrouping).
//
// Canonical keys, in their own namespaces so they never collide with a tag
// guess (product::… / swatch::…):
// - bucket::<group id>::solids|florals: listed in that Product Group bucket
// - verified::<tag key>: no Product Group metafield, so the tag stands
// - single::<product id>: not grouped (Home Try-On, or its Product Group
//   doesn't list it in solids/florals)

// Ungrouped product.
function singleCardKey(productId) {
  return `single::${productId}`;
}

function isSingleCardKey(key) {
  return key.startsWith("single::");
}

function productGroupIdForNode(node) {
  return node?.productGroup?.reference?.id || null;
}

// What the verification needs to know about one product.
function productRecord({ id, productType, productGroupId, tagKey }) {
  return { id, productType: productType || "", productGroupId: productGroupId || null, tagKey: tagKey || null };
}

function productRecordForNode(node) {
  return productRecord({
    id: node.id,
    productType: node.productType,
    productGroupId: productGroupIdForNode(node),
    tagKey: styleGroupKeyForNode(node),
  });
}

function productRecordForCard(card) {
  return productRecord({
    id: card.dataset.productId,
    productType: card.dataset.productType,
    productGroupId: card.dataset.productGroupId,
    tagKey: styleGroupKeyForCard(card),
  });
}

// productGroupId -> Promise<Map<productId, { key, memberIds }> | null>
// (null: the request failed, so those products can't be verified yet).
const productGroupCache = new Map();

function collectBuckets(node, entries) {
  if (!node) {
    return;
  }

  ["solids", "florals"].forEach((type) => {
    const memberIds = (node[type]?.references?.nodes || []).map((product) => product?.id).filter(Boolean);
    const key = `bucket::${node.id}::${type}`;

    memberIds.forEach((id) => {
      entries.set(id, { key, memberIds });
    });
  });

  (node.options?.references?.nodes || []).forEach((child) => collectBuckets(child, entries));
}

function loadProductGroups(groupIds) {
  const missing = groupIds.filter((id) => !productGroupCache.has(id));

  chunk(missing, maxIdsPerNodesRequest).forEach((batchIds) => {
    const request = storefront
      .request({ query: PRODUCT_CARD_GROUPING_NODES_QUERY, variables: { ids: batchIds } })
      .then((response) => {
        const nodes = response?.data?.nodes;

        if (!Array.isArray(nodes)) {
          throw new Error(`Product Group request failed: ${JSON.stringify(response?.errors || response)}`);
        }

        return nodes;
      })
      .catch((error) => {
        console.error("[PLP grouping] Product Group request failed:", error);

        batchIds.forEach((id) => productGroupCache.delete(id));

        return null;
      });

    batchIds.forEach((id, index) => {
      productGroupCache.set(
        id,
        request.then((nodes) => {
          if (!nodes) {
            return null;
          }

          const entries = new Map();

          collectBuckets(nodes[index], entries);

          return entries;
        }),
      );
    });
  });

  return Promise.all(groupIds.map(async (id) => [id, await productGroupCache.get(id)]));
}

// records -> Map<productId, { key, memberIds? }>. Products whose Product Group
// couldn't be loaded get no entry: they keep their tag grouping for now.
async function resolveCanonicalKeys(records) {
  const groupIds = [...new Set(records.map((record) => record.productGroupId).filter(Boolean))];
  const groups = new Map(groupIds.length ? await loadProductGroups(groupIds) : []);
  const resolved = new Map();

  records.forEach(({ id, productType, productGroupId, tagKey }) => {
    if (isHomeTryOnType(productType)) {
      resolved.set(id, { key: singleCardKey(id) });

      return;
    }

    if (!productGroupId) {
      resolved.set(id, { key: tagKey ? `verified::${tagKey}` : singleCardKey(id) });

      return;
    }

    const entries = groups.get(productGroupId);

    if (entries) {
      resolved.set(id, entries.get(id) || { key: singleCardKey(id) });
    }
  });

  return resolved;
}

function resolveDisplayPrice(price, isSwatchProduct) {
  if (isSwatchProduct && !window.customerHasFreeSwatch) {
    return { price: 0, compareAtPrice: price };
  }

  return { price, compareAtPrice: 0 };
}

function numericIdFromGid(gid) {
  const match = typeof gid === "string" && gid.match(/(\d+)$/);

  return match ? match[1] : null;
}

function extractColorFamilyIds(colorFamilyListField) {
  return (colorFamilyListField?.references?.nodes || []).map((node) => node?.id).filter(Boolean);
}

// Mirrors snippet.product-card.liquid: a single-value delivery_time (or a list
// holding exactly one) becomes the card's quick-add delivery option.
function extractDeliveryOption(deliveryTimeField) {
  const value = deliveryTimeField?.value;

  if (!value) {
    return null;
  }

  if (!value.startsWith("[")) {
    return value;
  }

  try {
    const list = JSON.parse(value);

    return Array.isArray(list) && list.length === 1 ? String(list[0]) : null;
  } catch (_) {
    return null;
  }
}

// Storefront API image URLs point at the original upload (often 3000px+);
// the CDN resizes on a width param, matching what Liquid renders.
function sizedImageUrl(url, width) {
  if (!url) {
    return null;
  }

  try {
    const sized = new URL(url);

    sized.searchParams.set("width", String(width));

    return sized.toString();
  } catch (_) {
    return url;
  }
}

// Paint-only swatch data (PRODUCT_SWATCH_DOT_FIELDS_FRAGMENT). `partial`: the
// product's images/price/variant still have to be hydrated before the card
// can switch to it (see selectSwatch).
function extractSwatchDot(node) {
  const colorPatternNode = node.colorPattern?.references?.nodes?.[0];

  return {
    partial: true,
    productId: node.id,
    title: node.title || "",
    url: node.handle ? `/products/${node.handle}` : "#",
    available: node.availableForSale !== false,
    swatchImageUrl: sizedImageUrl(colorPatternNode?.swatchImage?.reference?.image?.url, 64),
    swatchColor: colorPatternNode?.swatchColor?.value || null,
    swatchLabel: colorPatternNode?.swatchLabel?.value || null,
    colorFamilyIds: extractColorFamilyIds(node.colorFamilyList),
  };
}

function extractHydratedSwatchData(node) {
  if (!node) {
    return null;
  }

  const colorPatternNode = node.colorPattern?.references?.nodes?.[0];
  const images = node.images?.nodes || [];

  return {
    hydrated: true,
    productId: node.id,
    title: node.title || "",
    url: node.handle ? `/products/${node.handle}` : "#",
    available: node.availableForSale !== false,
    imageUrl: sizedImageUrl(images[0]?.url || node.featuredImage?.url, cardImageWidth),
    imageAlt: images[0]?.altText || node.featuredImage?.altText || node.title || "",
    hoverImageUrl: sizedImageUrl(images[1]?.url, cardImageWidth),
    price: node.priceRange?.minVariantPrice?.amount ? Number(node.priceRange.minVariantPrice.amount) : null,
    currencyCode: node.priceRange?.minVariantPrice?.currencyCode || null,
    swatchImageUrl: sizedImageUrl(colorPatternNode?.swatchImage?.reference?.image?.url, 64),
    swatchColor: colorPatternNode?.swatchColor?.value || null,
    swatchLabel: colorPatternNode?.swatchLabel?.value || null,

    color: colorPatternNode?.swatchLabel?.value || null,
    colorCode: colorPatternNode?.swatchLabel?.value?.split(" - ")?.pop()?.trim() || null,
    styleGroup: getStyleGroupTag(node.tags) || null,
    colorFamilyIds: extractColorFamilyIds(node.colorFamilyList),
    deliveryOption: extractDeliveryOption(node.deliveryTime),
    variantId: numericIdFromGid(node.variants?.nodes?.[0]?.id),
    isSwatchProduct: isSwatchProductFromTags(node.tags),
  };
}

function applyAddToBagTarget(
  card,
  { variantId, isSwatchProduct, color, colorCode, styleGroup, deliveryOption, forceShowOnGrouped = false } = {},
) {
  const button = card.querySelector("[data-product-card-add-to-bag]");

  if (!button) {
    return;
  }

  delete button.dataset.state;
  button.disabled = false;

  const isAmbiguouslyGrouped = Boolean(card.dataset.groupedBucketKey) && !forceShowOnGrouped;

  if (!isAmbiguouslyGrouped && isSwatchProduct && variantId) {
    button.dataset.variantId = variantId;

    if (color) {
      button.dataset.color = color;
    } else {
      delete button.dataset.color;
    }

    if (colorCode) {
      button.dataset.colorCode = colorCode;
    } else {
      delete button.dataset.colorCode;
    }

    if (styleGroup) {
      button.dataset.styleGroup = styleGroup;
    } else {
      delete button.dataset.styleGroup;
    }

    // Carried per product (captured from its server-rendered card) so the
    // quick-add line item gets the same delivery time properties as the PDP.
    if (deliveryOption) {
      button.dataset.deliveryOption = deliveryOption;
    } else {
      delete button.dataset.deliveryOption;
    }

    button.hidden = false;
  } else {
    button.hidden = true;
    delete button.dataset.variantId;
    delete button.dataset.color;
    delete button.dataset.colorCode;
    delete button.dataset.styleGroup;
    delete button.dataset.deliveryOption;
  }
}

async function hydrateSwatchProducts(productIds) {
  const idsToFetch = productIds.filter((id) => !hydratedSwatchCache.has(id));

  if (idsToFetch.length) {
    const batches = chunk(idsToFetch, maxIdsPerNodesRequest);

    const responses = await Promise.all(
      batches.map((batchIds) =>
        storefront
          .request({
            query: PRODUCT_SWATCH_COLOR_PATTERNS_QUERY,
            variables: { ids: batchIds },
          })
          .then((response) => ({ batchIds, response }))
          .catch((error) => {
            console.error("[PLP grouping] Failed hydrating swatches:", error);

            return { batchIds, response: null };
          }),
      ),
    );

    responses.forEach(({ batchIds, response }) => {
      const nodes = response?.data?.nodes;

      if (!Array.isArray(nodes)) {
        return;
      }

      batchIds.forEach((id, index) => {
        hydratedSwatchCache.set(id, extractHydratedSwatchData(nodes[index]));
      });
    });
  }

  return productIds.map((id) => hydratedSwatchCache.get(id)).filter(Boolean);
}

function applyColorPatternToSwatch(button, swatchData) {
  if (!button || !swatchData) {
    return;
  }

  if (swatchData.swatchImageUrl) {
    button.style.backgroundColor = "";
    button.style.backgroundImage = `url('${swatchData.swatchImageUrl}')`;
    button.style.backgroundSize = "cover";
    button.style.backgroundPosition = "center";
    return;
  }

  if (swatchData.swatchColor) {
    button.style.backgroundImage = "none";
    button.style.backgroundColor = swatchData.swatchColor;
  }
}

function productMatchesColorFamily(data, activeFamilyId) {
  return (data?.colorFamilyIds || []).includes(activeFamilyId);
}

// Which products belong to each group, used for the "+N" total, for padding a
// card's swatches with colours that aren't on a loaded page, and (collection)
// for the grouped count. Holds both the tag guess (product::… keys) and, once
// verified, the canonical Product Group grouping (bucket::… / verified::…).
//
// - Collection (collectionHandle set): one scan of the collection's ids, tags,
//   Product Group and swatch dot, then its Product Groups. Cached for the
//   session, so a repeat visit groups canonically on first paint.
// - Search / all products: a tag lookup for just the groups on screen, and
//   the Product Groups of the cards on screen.
export function createStyleGroupIndex({ collectionHandle = null } = {}) {
  const members = new Map();
  const swatches = new Map();
  const canonicalKeys = new Map();
  const lookups = new Map();
  const collectionIds = new Set();
  const cacheKey = collectionHandle ? `${styleGroupIndexCachePrefix}${collectionHandle}` : null;
  const watchers = new Set();
  const pendingHydration = new Set();
  let hydrationTimer = null;
  let collectionScan = null;
  let collectionVerification = null;
  let resolveFirstPage;
  const firstPage = new Promise((resolve) => {
    resolveFirstPage = resolve;
  });

  // --- Swatch fill ---------------------------------------------------------
  // Grouped cards watch the index: every time it learns more (a scan page, a
  // tag lookup, a verified group) each one fills up to maxVisibleSwatches
  // from what's known so far and updates its "+N". Swatches are only ever
  // added, in the group's collection order, so a card goes 1 → 4 dots once,
  // not one dot at a time per request.

  const knownSwatch = (productId) => hydratedSwatchCache.get(productId) || swatches.get(productId) || null;

  // Members the index has no swatch dot for (Product Group members on
  // search): one batched request, then another fill.
  function queueHydration(productIds) {
    productIds.forEach((id) => {
      if (!hydratedSwatchCache.has(id)) {
        pendingHydration.add(id);
      }
    });

    if (!pendingHydration.size || hydrationTimer) {
      return;
    }

    hydrationTimer = setTimeout(() => {
      const ids = [...pendingHydration];

      pendingHydration.clear();
      hydrationTimer = null;

      hydrateSwatchProducts(ids)
        .then(refreshWatchers)
        .catch((error) => {
          console.error("[PLP grouping] Failed hydrating swatches:", error);
        });
    }, 0);
  }

  function fill(representative) {
    const key = representative.dataset.groupedBucketKey;
    const container = representative.querySelector("[data-product-card-swatches]");

    if (!key || !container) {
      return;
    }

    const existingIds = new Set(
      Array.from(container.querySelectorAll(".product-card__swatch"))
        .map((swatch) => swatch.dataset.productId)
        .filter(Boolean),
    );
    const memberIds = members.get(key) || [];

    // style_group is only a first-paint guess. Do not expose its member total
    // because it can be larger than the canonical Product Group bucket and
    // causes +N to jump backwards after verification (for example +61 -> +53).
    // Only canonical Product Group / verified-tag keys may drive the counter.
    const isCanonicalKey = key.startsWith("bucket::") || key.startsWith("verified::");

    if (isCanonicalKey) {
      representative.dataset.groupTotalCount = String(new Set([...existingIds, ...memberIds]).size);
    } else {
      representative.dataset.groupTotalCount = String(Math.min(existingIds.size, maxVisibleSwatches));
    }

    const missingSlots = maxVisibleSwatches - existingIds.size;

    if (missingSlots > 0) {
      const candidateIds = memberIds.filter((id) => !existingIds.has(id));
      const familyId = representative.__swatchFamilyId;

      queueHydration(candidateIds.filter((id) => !knownSwatch(id)));

      let candidates = candidateIds.map(knownSwatch).filter(Boolean);

      // Colour filter active: that family's colours first.
      if (familyId) {
        candidates = candidates.sort(
          (a, b) => Number(productMatchesColorFamily(b, familyId)) - Number(productMatchesColorFamily(a, familyId)),
        );
      }

      candidates.slice(0, missingSlots).forEach((data) => addSwatch(representative, data));
    }

    updateSwatchVisibility(container, representative);
  }

  function refreshWatchers() {
    watchers.forEach((representative) => {
      if (representative.isConnected) {
        fill(representative);
      } else {
        watchers.delete(representative);
      }
    });
  }

  function addMemberIds(key, productIds) {
    const ids = members.get(key) || [];

    productIds.forEach((id) => {
      if (!ids.includes(id)) {
        ids.push(id);
      }
    });

    if (ids.length) {
      members.set(key, ids);
    }
  }

  function readCache() {
    try {
      const cached = JSON.parse(window.sessionStorage.getItem(cacheKey) || "null");

      if (!cached || Date.now() - cached.ts > styleGroupIndexCacheTtlMs) {
        return null;
      }

      cached.members.forEach(([key, ids]) => addMemberIds(key, ids));
      cached.swatches.forEach(([id, dot]) => swatches.set(id, dot));
      cached.canonicalKeys.forEach(([id, key]) => canonicalKeys.set(id, key));
      cached.collectionIds.forEach((id) => collectionIds.add(id));

      return cached;
    } catch (_) {
      return null;
    }
  }

  function writeCache(predictedCount, verifiedCount) {
    try {
      window.sessionStorage.setItem(
        cacheKey,
        JSON.stringify({
          ts: Date.now(),
          predictedCount,
          verifiedCount,
          members: [...members],
          swatches: [...swatches],
          canonicalKeys: [...canonicalKeys],
          collectionIds: [...collectionIds],
        }),
      );
    } catch (_) {
      // Storage full or unavailable: the next visit just scans again.
    }
  }

  // Resolves { predicted, verification }: the tag count as soon as the scan
  // ends, and a promise of the Product Group count.
  async function runCollectionScan({ onProgress = null } = {}) {
    const counter = createGroupedCollectionCounter();
    const order = [];

    await forEachConnectionPage({
      request: (after) =>
        storefront.request({
          query: COLLECTION_STYLE_GROUP_INDEX_QUERY,
          variables: { handle: collectionHandle, first: after ? 250 : firstIndexPageSize, after },
        }),
      getConnection: (response) => response?.data?.collection?.products,
      maxPages: maxCollectionIndexPages,
      onPage: (nodes, progress) => {
        const count = counter.addProducts(nodes);

        nodes.forEach((node) => {
          if (!node?.id || isExcludedProduct(node)) {
            return;
          }

          const tagKey = styleGroupKeyForNode(node);

          collectionIds.add(node.id);
          order.push(node.id);

          if (tagKey) {
            addMemberIds(tagKey, [node.id]);
          }

          if (tagKey || productGroupIdForNode(node)) {
            swatches.set(node.id, extractSwatchDot(node));
          }
        });

        // Resolve this page against Product Group immediately and add only
        // products actually discovered by the collection scan to canonical
        // buckets. This makes +N grow progressively (+6 -> +18 -> ...)
        // without ever exposing the broader style_group guess.
        const pageRecords = nodes.filter((node) => node?.id && !isExcludedProduct(node)).map(productRecordForNode);

        resolveCanonicalKeys(pageRecords)
          .then((resolved) => {
            if (!resolved.size) {
              return;
            }

            pageRecords.forEach(({ id }) => {
              const result = resolved.get(id);

              if (!result) {
                return;
              }

              canonicalKeys.set(id, result.key);

              if (!isSingleCardKey(result.key)) {
                // Deliberately add only this scanned product, not result.memberIds.
                // The visible counter therefore reflects scan progress and can
                // only increase toward the final canonical total.
                addMemberIds(result.key, [id]);
              }
            });

            refreshWatchers();
          })
          .catch((error) => {
            console.error("[PLP grouping] Failed progressive Product Group verification:", error);
          });

        refreshWatchers();
        resolveFirstPage();

        onProgress?.(count, progress);
      },
    }).finally(resolveFirstPage);

    const predicted = counter.count;

    const verification = counter.verify().then((verified) => {
      order.forEach((id) => {
        const key = counter.keyFor(id);

        canonicalKeys.set(id, key);

        if (!isSingleCardKey(key)) {
          addMemberIds(key, [id]);
        }
      });

      writeCache(predicted, verified);
      refreshWatchers();

      return verified;
    });

    return { predicted, verification };
  }

  // A fresh session cache answers straight away; the scan still runs in the
  // background to refresh it for the next visit.
  function scanCollection({ onProgress = null } = {}) {
    if (collectionScan) {
      return collectionScan;
    }

    if (cached) {
      resolveFirstPage();
      collectionScan = Promise.resolve(cached.predictedCount);
      collectionVerification = Promise.resolve(cached.verifiedCount);

      runCollectionScan()
        .then(({ verification }) => verification)
        .catch((error) => {
          console.error("[PLP grouping] Background style group refresh failed:", error);
        });

      return collectionScan;
    }

    const run = runCollectionScan({ onProgress });

    collectionScan = run.then(({ predicted }) => predicted);
    collectionVerification = run.then(({ verification }) => verification);

    collectionScan.catch((error) => {
      collectionScan = null;
      collectionVerification = null;

      console.error("[PLP grouping] Style group scan failed:", error);
    });

    return collectionScan;
  }

  function lookupByTag(styleGroups) {
    const pending = [...styleGroups].filter(([key]) => !lookups.has(key));

    chunk(pending, maxStyleGroupsPerLookup).forEach((batch) => {
      const batchKeys = new Set(batch.map(([key]) => key));
      const query = batch
        .map(([, styleGroup]) => `tag:"${escapeForQuotedValue(`${styleGroupTagPrefix}${styleGroup}`)}"`)
        .join(" OR ");

      const request = forEachConnectionPage({
        request: (after) =>
          storefront.request({ query: STYLE_GROUP_MEMBERS_QUERY, variables: { query, first: 250, after } }),
        getConnection: (response) => response?.data?.products,
        maxPages: maxStyleGroupLookupPages,
        onPage: (nodes) => {
          nodes.forEach((node) => {
            const tagKey = styleGroupKeyForNode(node);

            if (!batchKeys.has(tagKey) || isExcludedProduct(node)) {
              return;
            }

            addMemberIds(tagKey, [node.id]);
            swatches.set(node.id, extractSwatchDot(node));

            // Without a Product Group the tag is also the canonical grouping.
            if (!productGroupIdForNode(node)) {
              addMemberIds(`verified::${tagKey}`, [node.id]);
            }
          });
        },
      })
        .then(refreshWatchers)
        .catch((error) => {
          console.error("[PLP grouping] Style group lookup failed:", error);

          batchKeys.forEach((key) => lookups.delete(key));
        });

      batchKeys.forEach((key) => lookups.set(key, request));
    });

    return Promise.all([...styleGroups.keys()].map((key) => lookups.get(key)));
  }

  // Read up front, so the very first grouping pass already uses the
  // verified keys from the last visit.
  const cached = collectionHandle ? readCache() : null;

  async function awaitCollectionVerification() {
    try {
      await scanCollection();

      return await collectionVerification;
    } catch (_) {
      return null;
    }
  }

  if (!collectionHandle) {
    resolveFirstPage();
  }

  return {
    // Resolves once the first scan page is in (right away when cached, on
    // search, or if the scan fails), for the first paint to wait on.
    firstPage,

    // Keeps a grouped card's swatches filled as the index learns more.
    watch(representative, activeFamilyId = null) {
      representative.__swatchFamilyId = activeFamilyId;
      watchers.add(representative);

      if (collectionHandle) {
        scanCollection();
      }

      fill(representative);
    },

    // Canonical key when known (verified, or from the session cache), else
    // the tag guess. null: the card isn't grouped.
    keyForCard(card) {
      const canonical = canonicalKeys.get(card.dataset.productId);

      if (canonical) {
        return isSingleCardKey(canonical) ? null : canonical;
      }

      return styleGroupKeyForCard(card);
    },

    count: (options) => (collectionHandle ? scanCollection(options) : Promise.resolve(null)),

    verifiedCount: () => (collectionHandle ? awaitCollectionVerification() : Promise.resolve(null)),

    // records -> Map<productId, canonical key>; products that couldn't be
    // verified are left out.
    async canonicalKeysFor(records) {
      if (collectionHandle) {
        await awaitCollectionVerification();
      }

      const unknown = records.filter((record) => !canonicalKeys.has(record.id));
      const resolved = await resolveCanonicalKeys(unknown);

      resolved.forEach(({ key, memberIds }, id) => {
        canonicalKeys.set(id, key);

        if (!memberIds) {
          if (!isSingleCardKey(key)) {
            addMemberIds(key, [id]);
          }

          return;
        }

        const eligibleIds = collectionHandle ? memberIds.filter((memberId) => collectionIds.has(memberId)) : memberIds;

        addMemberIds(key, eligibleIds);

        // The rest of the bucket is verified too, so a member arriving on a
        // later page merges straight into this card instead of the tag's.
        eligibleIds.forEach((memberId) => {
          if (!canonicalKeys.has(memberId)) {
            canonicalKeys.set(memberId, key);
          }
        });
      });

      refreshWatchers();

      return new Map(records.filter(({ id }) => canonicalKeys.has(id)).map(({ id }) => [id, canonicalKeys.get(id)]));
    },

    // styleGroups: Map of tag key -> style_group tag value, for the search
    // tag lookup. Resolves { members: key -> productIds, swatches: productId
    // -> swatch dot }.
    async membersFor(styleGroups = new Map()) {
      try {
        await (collectionHandle ? scanCollection() : lookupByTag(styleGroups));
      } catch (error) {
        console.error("[PLP grouping] Failed resolving style group members:", error);
      }

      return { members, swatches };
    },
  };
}

function captureCardData(card) {
  const titleLink = card.querySelector("[data-product-card-title]");
  const imageEl = card.querySelector("[data-product-card-image]");
  const hoverImageEl = card.querySelector("[data-product-card-image-hover]");
  const badgesEl = card.querySelector("[data-product-card-badges]");
  const priceEl = card.querySelector("price-snippet");
  const addToBagBtn = card.querySelector("[data-product-card-add-to-bag]");
  const imageHtml = imageEl ? imageEl.innerHTML : "";

  return {
    productId: card.dataset.productId,
    available: card.dataset.available !== "false",
    url: titleLink ? titleLink.getAttribute("href") : "#",
    title: titleLink ? titleLink.textContent.trim() : "",
    imageHtml,
    hoverImageHtml: hoverImageEl ? hoverImageEl.innerHTML : "",
    badgesHtml: badgesEl ? badgesEl.innerHTML : "",
    priceOuterHtml: priceEl ? priceEl.outerHTML : "",
    // Rendered onto the card itself (snippet.product-card.liquid, or
    // hydrateApiProductCard), so a grouped card's swatches need no request.
    swatchImageUrl: card.dataset.swatchImage || null,
    swatchColor: card.dataset.swatchColor || null,
    swatchLabel: card.dataset.color || "",

    variantId: addToBagBtn ? addToBagBtn.dataset.variantId || null : null,
    color: addToBagBtn ? addToBagBtn.dataset.color || null : null,
    colorCode: addToBagBtn ? addToBagBtn.dataset.colorCode || null : null,
    styleGroup: addToBagBtn ? addToBagBtn.dataset.styleGroup || null : null,
    deliveryOption: addToBagBtn ? addToBagBtn.dataset.deliveryOption || null : null,
    isSwatchProduct: Boolean(addToBagBtn && !addToBagBtn.hidden),
  };
}

function applyHydratedProduct(card, data, forceShowOnGrouped = false) {
  const imageContainer = card.querySelector("[data-product-card-image]");

  if (imageContainer && data.imageUrl) {
    imageContainer.querySelectorAll("source").forEach((source) => {
      source.srcset = data.imageUrl;
    });

    const image = imageContainer.querySelector("img");

    if (image) {
      image.src = data.imageUrl;
      image.srcset = data.imageUrl;

      if (data.imageAlt) {
        image.alt = data.imageAlt;
      }
    }
  }

  const hoverContainer = card.querySelector("[data-product-card-image-hover]");

  if (hoverContainer) {
    if (data.hoverImageUrl) {
      hoverContainer.querySelectorAll("source").forEach((source) => {
        source.srcset = data.hoverImageUrl;
      });

      const hoverImage = hoverContainer.querySelector("img");

      if (hoverImage) {
        hoverImage.src = data.hoverImageUrl;
        hoverImage.srcset = data.hoverImageUrl;
        hoverImage.alt = data.imageAlt || "";
      }

      hoverContainer.hidden = false;
    } else {
      hoverContainer.hidden = true;
    }
  }

  card.querySelectorAll("[data-product-card-link]").forEach((link) => {
    link.setAttribute("href", data.url);
  });

  const titleLink = card.querySelector("[data-product-card-title]");

  if (titleLink) {
    titleLink.setAttribute("href", data.url);
  }

  if (data.price !== null) {
    const priceEl = card.querySelector("price-snippet");

    if (priceEl) {
      const { price, compareAtPrice } = resolveDisplayPrice(data.price, data.isSwatchProduct);

      priceEl.setAttribute("price", String(price));
      priceEl.setAttribute("currency", data.currencyCode || "USD");
      priceEl.setAttribute("compare-at-price", String(compareAtPrice));

      priceEl.dataset.basePrice = String(price);
      priceEl.dataset.baseCompareAtPrice = String(compareAtPrice);
      priceEl.dataset.rawPrice = String(data.price);
      priceEl.dataset.swatchProduct = String(Boolean(data.isSwatchProduct));
      priceEl.dataset.freePrice = String(Boolean(data.isSwatchProduct) && !window.customerHasFreeSwatch);
    }
  }

  const wishlistBtn = card.querySelector("[data-add-to-wishlist]");

  if (wishlistBtn) {
    wishlistBtn.dataset.productId = data.productId;
    wishlistBtn.dataset.wishlistProductId = numericIdFromGid(data.productId) || "";
    wishlistBtn.dataset.wishlistVariantId = data.variantId || "";
    wishlistBtn.dataset.wishlistProductUrl = data.url ? `${window.location.origin}${data.url}` : "";
  }

  applyAddToBagTarget(card, { ...data, forceShowOnGrouped });
}

export function hydrateApiProductCard(card, node, { childMode = false } = {}) {
  if (!card || !node) {
    return;
  }

  const data = extractHydratedSwatchData(node);

  if (!data) {
    return;
  }

  data.hydrated = true;
  card.dataset.productId = node.id;
  card.dataset.available = node.availableForSale === false ? "false" : "true";

  const productGroupId = node.productGroup?.reference?.id;

  if (productGroupId) {
    card.dataset.productGroupId = productGroupId;
  } else {
    delete card.dataset.productGroupId;
  }

  delete card.dataset.groupedBucketKey;
  delete card.dataset.groupTotalCount;

  // Same grouping + swatch inputs snippet.product-card.liquid renders.
  card.dataset.productType = node.productType || "";
  card.dataset.styleGroup = data.styleGroup || "";
  card.dataset.swatchProduct = String(data.isSwatchProduct);
  card.dataset.color = data.color || "";

  if (data.swatchImageUrl) {
    card.dataset.swatchImage = data.swatchImageUrl;
  } else {
    delete card.dataset.swatchImage;
  }

  if (data.swatchColor) {
    card.dataset.swatchColor = data.swatchColor;
  } else {
    delete card.dataset.swatchColor;
  }

  if (isHomeTryOnType(node.productType)) {
    card.dataset.homeTryOn = "true";
  } else {
    delete card.dataset.homeTryOn;
  }

  // The template card is cloned from whichever product rendered first, which
  // may not have had a swatch row.
  if (data.styleGroup && !card.querySelector("[data-product-card-swatches]")) {
    const swatchesContainer = document.createElement("div");

    swatchesContainer.className = "product-card__swatches";
    swatchesContainer.dataset.productCardSwatches = "";
    swatchesContainer.setAttribute("aria-label", "Available colors");

    card.querySelector("price-snippet")?.after(swatchesContainer);
  }

  card.removeAttribute("data-api-filter-hidden");
  card.removeAttribute(pendingRevealAttr);
  card.hidden = false;

  applyHydratedProduct(card, data);

  const titleLink = card.querySelector("[data-product-card-title]");

  if (titleLink) {
    titleLink.textContent = String(node.title || "")
      .split(" | ")[0]
      .trim();
  }

  const badges = card.querySelector("[data-product-card-badges]");

  if (badges) {
    badges.innerHTML = "";
    badges.style.display = "none";
  }

  if (childMode) {
    const swatchesContainer = card.querySelector("[data-product-card-swatches]");

    if (swatchesContainer) {
      swatchesContainer.innerHTML = "";

      addSwatch(card, data, { isActive: true });
    }
  }
}

function applyActiveProduct(card, data, forceShowOnGrouped = false) {
  card.dataset.productId = data.productId;
  card.dataset.available = data.available ? "true" : "false";

  if (data.hydrated) {
    applyHydratedProduct(card, data, forceShowOnGrouped);

    return;
  }

  const imageEl = card.querySelector("[data-product-card-image]");
  const hoverImageEl = card.querySelector("[data-product-card-image-hover]");
  const badgesEl = card.querySelector("[data-product-card-badges]");
  const priceEl = card.querySelector("price-snippet");
  const wishlistBtn = card.querySelector("[data-add-to-wishlist]");

  if (imageEl) {
    imageEl.innerHTML = data.imageHtml;
  }

  if (hoverImageEl) {
    hoverImageEl.innerHTML = data.hoverImageHtml;
  }

  if (badgesEl) {
    badgesEl.innerHTML = data.badgesHtml;
    badgesEl.style.display = data.badgesHtml ? "" : "none";
  }

  if (priceEl && data.priceOuterHtml) {
    const newPriceEl = new DOMParser().parseFromString(data.priceOuterHtml, "text/html").querySelector("price-snippet");

    if (newPriceEl) {
      priceEl.replaceWith(newPriceEl);

      // data.priceOuterHtml was captured once (see captureCardData) and can be stale relative
      // to the live cart by the time a shopper clicks this swatch — resync it against the
      // current free-swatch state rather than trusting the snapshot.
      window.syncSwatchPriceSnippet?.(newPriceEl);
    }
  }

  card.querySelectorAll("[data-product-card-link]").forEach((link) => {
    link.setAttribute("href", data.url);

    if (link.hasAttribute("data-product-card-title")) {
      link.textContent = data.title;
    }
  });

  if (wishlistBtn) {
    wishlistBtn.dataset.productId = data.productId;

    if (data.variantId) {
      wishlistBtn.dataset.wishlistProductId = numericIdFromGid(data.productId) || "";
      wishlistBtn.dataset.wishlistVariantId = data.variantId;
      wishlistBtn.dataset.wishlistProductUrl = data.url ? `${window.location.origin}${data.url}` : "";
    }
  }

  applyAddToBagTarget(card, { ...data, forceShowOnGrouped });
}

function updateSwatchVisibility(swatchesContainer, representative) {
  const buttons = Array.from(swatchesContainer.querySelectorAll(".product-card__swatch"));

  let moreEl = swatchesContainer.querySelector(".product-card__swatch-more");

  buttons.forEach((button, index) => {
    button.style.display = index < maxVisibleSwatches ? "" : "none";
  });

  const totalCount = Number.parseInt(representative.dataset.groupTotalCount, 10);
  const knownCount = Number.isNaN(totalCount) ? buttons.length : totalCount;
  const remaining = Math.max(0, knownCount - maxVisibleSwatches);

  if (remaining > 0) {
    if (!moreEl) {
      moreEl = document.createElement("a");
      moreEl.className = "product-card__swatch-more body-xsmall";

      swatchesContainer.appendChild(moreEl);
    }

    moreEl.textContent = `+${remaining}`;

    const titleLink = representative.querySelector("[data-product-card-title]");

    moreEl.setAttribute("href", titleLink ? titleLink.getAttribute("href") : "#");
  } else if (moreEl) {
    moreEl.remove();
  }
}

// Swaps a padded swatch's paint-only dot for the full product data (images,
// price, variant) so the card can switch to it.
async function hydratePartialSwatches(buttons) {
  const partial = buttons.filter((button) => button.__productCardData?.partial);

  if (!partial.length) {
    return;
  }

  const hydrated = await hydrateSwatchProducts(partial.map((button) => button.dataset.productId));
  const byId = new Map(hydrated.map((data) => [data.productId, data]));

  partial.forEach((button) => {
    const data = byId.get(button.dataset.productId);

    if (data) {
      button.__productCardData = data;
    }
  });
}

// Hovering (or focusing) a card fetches its padded swatches' full data ahead
// of a click, instead of every grid load paying for it.
function prefetchPartialSwatchesOnIntent(representative) {
  if (representative.dataset.swatchPrefetchBound === "true") {
    return;
  }

  representative.dataset.swatchPrefetchBound = "true";

  const prefetch = () => {
    hydratePartialSwatches(Array.from(representative.querySelectorAll(".product-card__swatch"))).catch((error) => {
      console.error("[PLP grouping] Failed prefetching swatches:", error);
    });
  };

  representative.addEventListener("pointerenter", prefetch, { once: true });
  representative.addEventListener("focusin", prefetch, { once: true });
}

async function selectSwatch(representative, button) {
  if (button.__productCardData?.partial) {
    const token = (representative.__swatchSelectToken || 0) + 1;

    representative.__swatchSelectToken = token;

    await hydratePartialSwatches([button]);

    // A later click won, or the product couldn't be loaded.
    if (representative.__swatchSelectToken !== token || button.__productCardData?.partial) {
      return;
    }
  }

  const data = button.__productCardData;

  if (!data) return;

  applyActiveProduct(representative, data, representative.dataset.addToBagUnlocked === "true");

  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  swatchesContainer.querySelectorAll(".product-card__swatch").forEach((swatch) => {
    const isActive = swatch === button;

    swatch.classList.toggle("is-active", isActive);
    swatch.setAttribute("aria-current", isActive ? "true" : "false");
  });

  updateSwatchVisibility(swatchesContainer, representative);
}

// data: captureCardData() for a card already in the grid,
// extractHydratedSwatchData() (hydrated) for one fetched by id, or
// extractSwatchDot() (partial) straight from the style group index.
function addSwatch(representative, data, { isActive = false, sourceCard = null } = {}) {
  const swatchesContainer = representative.querySelector("[data-product-card-swatches]");

  if (!swatchesContainer) {
    return;
  }

  const existing = Array.from(swatchesContainer.querySelectorAll(".product-card__swatch")).find(
    (button) => button.dataset.productId === data.productId,
  );

  if (existing) {
    // Padded in from the index before its card loaded: point it at the card.
    if (sourceCard && !existing.__sourceCard) {
      existing.__sourceCard = sourceCard;
      existing.__productCardData = data;
    }

    return;
  }

  const button = document.createElement("button");

  button.type = "button";
  button.className = "product-card__swatch";

  if (isActive) {
    button.classList.add("is-active");
  }

  if (!data.available) {
    button.classList.add("is-sold-out");
  }

  button.setAttribute("aria-current", isActive ? "true" : "false");
  button.dataset.productId = data.productId;

  const label = data.swatchLabel || data.title;

  applyColorPatternToSwatch(button, data);

  if (label) {
    button.title = label;
    button.setAttribute("aria-label", label);
  }

  button.__productCardData = data;
  // The removed card this swatch stands for, when it was loaded in the grid.
  button.__sourceCard = sourceCard;

  if (data.partial) {
    prefetchPartialSwatchesOnIntent(representative);
  }

  button.addEventListener("click", () => {
    selectSwatch(representative, button).catch((error) => {
      console.error("[PLP grouping] Failed selecting swatch:", error);
    });
  });

  const moreEl = swatchesContainer.querySelector(".product-card__swatch-more");

  if (moreEl) {
    swatchesContainer.insertBefore(button, moreEl);
  } else {
    swatchesContainer.appendChild(button);
  }

  swatchesContainer.style.display = "";

  updateSwatchVisibility(swatchesContainer, representative);
}

function makeRepresentative(card, bucketKey, totalCount, specificColorSelected) {
  // Untouched copy, so verifyGrouping can rebuild this product's own card if
  // the Product Group says it belongs elsewhere.
  card.__pristine ||= card.cloneNode(true);

  const ownData = captureCardData(card);

  card.dataset.groupedBucketKey = bucketKey;

  // A product:: / swatch:: key is only the style_group prediction. Keep +N
  // hidden until Product Group verification relabels the card to a canonical
  // bucket:: / verified:: key. Cached canonical keys can show the total at once.
  const isCanonicalKey = bucketKey.startsWith("bucket::") || bucketKey.startsWith("verified::");
  card.dataset.groupTotalCount = String(isCanonicalKey ? totalCount : Math.min(totalCount, maxVisibleSwatches));

  if (specificColorSelected) {
    card.dataset.addToBagUnlocked = "true";
  } else {
    delete card.dataset.addToBagUnlocked;
  }

  applyAddToBagTarget(card, specificColorSelected ? { ...ownData, forceShowOnGrouped: true } : {});

  const swatchesContainer = card.querySelector("[data-product-card-swatches]");

  if (swatchesContainer) {
    swatchesContainer.innerHTML = "";
  }

  addSwatch(card, ownData, { isActive: true });
}

// Each card's arrival order in the grid (collection sort order), kept on the
// card and its clones. A grouped card sits where its earliest product would.
function assignGridOrder(grid) {
  grid.__gridOrderSeq ||= 0;

  grid.querySelectorAll("[data-product-card]:not([data-grid-order])").forEach((card) => {
    card.dataset.gridOrder = String(grid.__gridOrderSeq);
    grid.__gridOrderSeq += 1;
  });
}

function gridOrderOf(card) {
  return Number((card.__pristine || card).dataset.gridOrder);
}

function groupKeyForCard(card, styleGroupIndex) {
  return styleGroupIndex ? styleGroupIndex.keyForCard(card) : styleGroupKeyForCard(card);
}

function getUngroupedCards(grid, styleGroupIndex) {
  return Array.from(grid.querySelectorAll("[data-product-card]")).filter(
    (card) => !card.dataset.groupedBucketKey && groupKeyForCard(card, styleGroupIndex),
  );
}

function groupStyleCards(representatives, groupKey, cards, styleGroupIndex, activeFamilyId, specificColorSelected) {
  let representative = representatives.get(groupKey);
  let siblings = cards;

  if (!representative) {
    [representative, ...siblings] = cards;

    makeRepresentative(representative, groupKey, cards.length, specificColorSelected);
    representatives.set(groupKey, representative);
  }

  siblings.forEach((card) => {
    addSwatch(representative, captureCardData(card), { sourceCard: card });
    card.remove();
  });

  styleGroupIndex?.watch(representative, activeFamilyId);
}

// First paint: groups from each card's key right away — the verified Product
// Group key when the index already knows it (session cache, or verified
// earlier), otherwise the style_group: tag guess. Never waits on the network;
// extra swatches / "+N" fill in as the index resolves, and verifyGrouping
// corrects any tag guess afterwards.
export async function runGroupingPass(
  grid,
  styleGroupIndex = null,
  { activeColorFamily = null, specificColorSelected = false } = {},
) {
  assignGridOrder(grid);

  const activeFamilyId = activeColorFamily?.metaobjectId || null;
  const groups = new Map();
  const styleGroups = new Map();

  getUngroupedCards(grid, styleGroupIndex).forEach((card) => {
    const groupKey = groupKeyForCard(card, styleGroupIndex);

    if (groupKey === styleGroupKeyForCard(card)) {
      styleGroups.set(groupKey, card.dataset.styleGroup.trim());
    }

    if (!groups.has(groupKey)) {
      groups.set(groupKey, []);
    }

    groups.get(groupKey).push(card);
  });

  if (!groups.size) {
    return;
  }

  const representatives = new Map(
    Array.from(grid.querySelectorAll("[data-grouped-bucket-key]")).map((card) => [card.dataset.groupedBucketKey, card]),
  );

  // Starts the tag lookup (search) for these groups; cards fill as it lands.
  styleGroupIndex?.membersFor(styleGroups);

  groups.forEach((groupCards, groupKey) => {
    groupStyleCards(representatives, groupKey, groupCards, styleGroupIndex, activeFamilyId, specificColorSelected);
  });
}

// A grid card and the untouched cards of the products it stands for.
function gridUnits(grid) {
  return Array.from(grid.querySelectorAll("[data-product-card]")).map((card) => {
    const own = card.__pristine || card;
    const siblings = Array.from(card.querySelectorAll(".product-card__swatch"))
      .map((button) => button.__sourceCard)
      .filter(Boolean);

    return { card, sources: [own, ...siblings] };
  });
}

// Inserted hidden: the caller's visible range decides whether it shows.
function insertInGridOrder(grid, card) {
  card.setAttribute("hidden", "");
  card.removeAttribute(pendingRevealAttr);

  const order = gridOrderOf(card);
  const next = Array.from(grid.querySelectorAll("[data-product-card]")).find(
    (existing) => gridOrderOf(existing) > order,
  );
  const anchor =
    next || grid.querySelector("[data-plp-interstitial-templates]") || grid.querySelector("[data-pagination-nav]");

  if (anchor) {
    anchor.before(card);
  } else {
    grid.appendChild(card);
  }
}

// Drops the swatches padded in from the index (not products loaded in the
// grid) so they can be padded again from the verified members.
function clearPaddedSwatches(card) {
  const ownId = (card.__pristine || card).dataset.productId;

  card.querySelectorAll(".product-card__swatch").forEach((button) => {
    if (!button.__sourceCard && button.dataset.productId !== ownId) {
      button.remove();
    }
  });

  card.querySelector(".product-card__swatch-more")?.remove();
}

// Second pass, in the background: checks every grid card against the Product
// Group metafield and fixes the ones the tag guessed wrong. A card whose
// products all verify to the same group (and nothing else in that group is
// elsewhere in the grid) just takes the canonical key; only mismatched cards
// are rebuilt, each at its earliest product's position. Products whose Product
// Group can't be loaded are left as the tag grouped them.
//
// Resolves true when cards were added/removed, so the caller can re-apply its
// visible range, banners and pagination.
export async function verifyGrouping(
  grid,
  styleGroupIndex,
  { activeColorFamily = null, specificColorSelected = false, isCurrent = () => true } = {},
) {
  if (!styleGroupIndex) {
    return false;
  }

  const records = new Map();

  gridUnits(grid).forEach(({ sources }) => {
    sources.forEach((source) => records.set(source.dataset.productId, productRecordForCard(source)));
  });

  if (!records.size) {
    return false;
  }

  const keys = await styleGroupIndex.canonicalKeysFor([...records.values()]);

  if (!isCurrent()) {
    return false;
  }

  // The grid can change while the Product Groups load; work from what's in it now.
  const units = gridUnits(grid);
  const canonicalGroups = new Map();

  units.forEach(({ sources }) => {
    sources.forEach((source) => {
      const key = keys.get(source.dataset.productId);

      if (!key) {
        return;
      }

      if (!canonicalGroups.has(key)) {
        canonicalGroups.set(key, []);
      }

      canonicalGroups.get(key).push(source);
    });
  });

  const activeFamilyId = activeColorFamily?.metaobjectId || null;
  const rebuildKeys = new Set();
  const staleCards = [];

  units.forEach(({ card, sources }) => {
    const unitKeys = new Set(sources.map((source) => keys.get(source.dataset.productId)));

    if (unitKeys.has(undefined)) {
      return;
    }

    const [key] = unitKeys;
    const isGrouped = Boolean(card.dataset.groupedBucketKey);
    const matches =
      unitKeys.size === 1 && canonicalGroups.get(key).length === sources.length && isSingleCardKey(key) !== isGrouped;

    if (!matches) {
      unitKeys.forEach((unitKey) => rebuildKeys.add(unitKey));
      staleCards.push(card);

      return;
    }

    // Right products, tag-guess key: relabel (so later pages merge into it)
    // and re-pad its swatches from the verified members. No layout change.
    if (isGrouped && card.dataset.groupedBucketKey !== key) {
      card.dataset.groupedBucketKey = key;
      clearPaddedSwatches(card);
      styleGroupIndex.watch(card, activeFamilyId);
    }
  });

  if (!staleCards.length) {
    return false;
  }

  staleCards.forEach((card) => card.remove());

  [...rebuildKeys]
    .map((key) => [key, [...canonicalGroups.get(key)].sort((a, b) => gridOrderOf(a) - gridOrderOf(b))])
    .sort(([, a], [, b]) => gridOrderOf(a[0]) - gridOrderOf(b[0]))
    .forEach(([key, sources]) => {
      if (isSingleCardKey(key)) {
        insertInGridOrder(grid, sources[0]);

        return;
      }

      const [representative, ...siblings] = sources;

      insertInGridOrder(grid, representative);
      groupStyleCards(
        new Map(),
        key,
        [representative, ...siblings],
        styleGroupIndex,
        activeFamilyId,
        specificColorSelected,
      );
    });

  return true;
}

// Counts cards the grid would show for a product set: one per group, one per
// ungrouped product. addProducts gives the tag-guess count straight away;
// verify() loads the Product Groups and returns the canonical count. Needs
// each product's id, productType, tags and productGroup.
export function createGroupedCollectionCounter() {
  const records = new Map();
  const predictedKeys = new Set();
  let canonical = new Map();

  return {
    addProducts(products = []) {
      products.forEach((product) => {
        if (!product?.id || isExcludedProduct(product)) {
          return;
        }

        const record = productRecordForNode(product);

        records.set(product.id, record);
        predictedKeys.add(record.tagKey || singleCardKey(product.id));
      });

      return predictedKeys.size;
    },

    async verify() {
      canonical = await resolveCanonicalKeys([...records.values()]);

      return new Set([...records.values()].map((record) => this.keyFor(record.id))).size;
    },

    // Canonical key, or the tag guess for a product that couldn't be verified.
    keyFor(productId) {
      const record = records.get(productId);

      return canonical.get(productId)?.key || record?.tagKey || singleCardKey(productId);
    },

    get count() {
      return predictedKeys.size;
    },
  };
}
